import { findVariant } from './products';
import type { Cart, CartLine } from './types';

/**
 * The cart lives in the browser (ΩΞ-TILL-MERCH-001): a list of variant ids and counts under
 * STORAGE_KEYS.cartLines, shared with the static /cart page. Everything shown — title, price,
 * image — is read from the catalogue at the moment it is shown, so a stored line can never carry
 * a stale price, and the till prices the order again from the same file.
 */
export interface StoredLine {
  readonly variantId: string;
  readonly quantity: number;
}

/** The most of one variant a single order takes (the till refuses more). */
export const MAX_LINE_QUANTITY = 10;
/** Distinct lines in one cart (the till refuses more). */
export const MAX_CART_LINES = 20;

export function parseStoredLines(raw: string | null): StoredLine[] {
  let parsed: unknown;
  try {
    parsed = JSON.parse(raw ?? '[]');
  } catch {
    return [];
  }
  if (!Array.isArray(parsed)) return [];
  const out: StoredLine[] = [];
  for (const entry of parsed) {
    if (typeof entry !== 'object' || entry === null) continue;
    const { variantId, quantity } = entry as { variantId?: unknown; quantity?: unknown };
    if (typeof variantId !== 'string' || typeof quantity !== 'number' || !Number.isInteger(quantity)) continue;
    if (quantity < 1 || out.some((line) => line.variantId === variantId)) continue;
    out.push({ variantId, quantity: Math.min(quantity, MAX_LINE_QUANTITY) });
  }
  return out.slice(0, MAX_CART_LINES);
}

export function withQuantity(lines: readonly StoredLine[], variantId: string, quantity: number): StoredLine[] {
  const q = Math.min(Math.max(0, Math.trunc(quantity)), MAX_LINE_QUANTITY);
  if (q === 0) return lines.filter((line) => line.variantId !== variantId);
  if (lines.some((line) => line.variantId === variantId)) {
    return lines.map((line) => (line.variantId === variantId ? { variantId, quantity: q } : line));
  }
  return lines.length >= MAX_CART_LINES ? [...lines] : [...lines, { variantId, quantity: q }];
}

export function withAdded(lines: readonly StoredLine[], variantId: string, quantity = 1): StoredLine[] {
  const current = lines.find((line) => line.variantId === variantId)?.quantity ?? 0;
  return withQuantity(lines, variantId, current + quantity);
}

export function quantityOf(lines: readonly StoredLine[], variantId: string): number {
  return lines.find((line) => line.variantId === variantId)?.quantity ?? 0;
}

/** The cart as shown: lines the catalogue no longer sells are left out (and dropped on the next write). */
export function resolveCart(lines: readonly StoredLine[], find: typeof findVariant = findVariant): Cart {
  const resolved: CartLine[] = [];
  for (const line of lines) {
    const hit = find(line.variantId);
    if (hit === null || !hit.variant.available) continue;
    resolved.push({
      id: line.variantId,
      quantity: line.quantity,
      variantId: line.variantId,
      title: hit.product.title,
      variantTitle: hit.variant.title,
      price: hit.variant.price,
      image: hit.variant.image ?? hit.product.images[0]?.url,
      handle: hit.product.handle,
    });
  }
  const subtotal = resolved.reduce((sum, line) => sum + Math.round(line.price * 100) * line.quantity, 0) / 100;
  return { lines: resolved, subtotal, totalQuantity: resolved.reduce((sum, line) => sum + line.quantity, 0) };
}

/** Only the lines the catalogue still sells, as stored. */
export function sellableLines(lines: readonly StoredLine[], find: typeof findVariant = findVariant): StoredLine[] {
  return lines.filter((line) => find(line.variantId)?.variant.available === true);
}
